<template>
<!-- eslint-disable max-len -->
<edit-page
  name="Character"
  model-name="character"
  param="characterId"
  page-prefix="/blade/characters"
  :create="createCharacter"
  :fetch="getCharacter"
  :update="updateCharacterWrapper"
  :remove="deleteCharacter"
  :stub="characterStub"
  :validator="validator"
  :utils="utils">
<template slot-scope="{ model: character, validator }">
  <div v-if="character.isOldCharacter" style="width: 100%; border: 2px solid rgba(128, 0, 0, 0.8); padding: 10px;">
    <h3>Warning: This character is not centralized</h3>
    <div>This character is not in the centralized character repository and can not be modified.  You may promote it to the centralized character repository.</div>
    <b-btn @click="centralizeChar(character)">Copy To Centralized Database</b-btn>
  </div>
  <text-input label v-model.trim="character.name" :validator="validator.name" required autofocus>Name</text-input>

  <text-input label v-model.trim="character.title" :validator="validator.title" required>Title</text-input>

  <text-input
    label
    v-model.trim="character.nameplateText"
    placeholder="(defaults to Title, supports HTML)"
  >Nameplate Text (appears above dialogue)</text-input>

  <text-input label textarea v-model.trim="character.bio">Bio</text-input>

  <text-input disabled label textarea v-model="aliasList">Character Explorer Aliases ({{aliases.length}} found)</text-input>

  <div>
    <checkbox-input v-model="character.avatar" inline>Avatar</checkbox-input>
    <checkbox-input v-model="character.challengeIntro" inline>Section Intro</checkbox-input>
    <checkbox-input v-model="character.narrator" inline>Narrator</checkbox-input>
    <checkbox-input v-model="character.scrollImage" inline>Scroll Image</checkbox-input>
    <checkbox-input v-model="character.twoDimensional" inline>2D</checkbox-input>
  </div>

  <template v-if="character.avatar || character.narrator || character.scrollImage">
    <select-input
      label
      v-model="character.theme"
      :options="themeOptions"
      :display="o => ((o === 'starfighter') ? 'panoramic' : o)"
    >Theme (optional, used for filtering)</select-input>
  </template>

  <template v-if="character.avatar || character.narrator || character.scrollImage">
    <div class="form-row">
      <img v-if="character.image"
        class="form-image-character"
        :src="resolveAssetPath(character.image, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <div class="col">
        <asset-select-input
          label
          v-model="character.image"
          legacy-manifest-path="characters"
        >Default Image</asset-select-input>

        <div
          style="
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            gap: 0 16px;
          "
        >
          <checkbox-input
            v-model="character.view360"
          >Enable 360 Image View Support</checkbox-input>
          <select-input
            v-if="character.view360"
            v-model="character.view360ProjectionType"
            label
            disable-sort
            default-value="Equirectangular"
            :options="['Equirectangular', 'Cylindrical']"
          >
            Projection Type
          </select-input>
          <text-input
            v-if="character.view360"
            label
            no-count
            v-model="character.view360ViewportWidth"
          >360 Image Viewport Width Override (use CSS units) (e.g. 950px)</text-input>
        </div>

        <checkbox-input
          v-model="character.clickToExpandImage"
        >Click Image To Expand</checkbox-input>

        <div
          style="
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            gap: 0 16px;
          "
        >
          <select-input
            v-if="character.clickToExpandImage"
            v-model="character.expandedImageModalWidth"
            label
            disable-sort
            default-value="lg"
            :options="['lg', 'xl', 'md', 'sm', 'fullscreen']"
          >
            Expanded Image Modal Width
          </select-input>

          <text-input
            v-if="character.clickToExpandImage"
            label
            no-count
            v-model="character.expandedImageMaxHeightOverride"
          >Expanded Image Max Height Override (use CSS units) (e.g. 72vh or 650px)</text-input>

          <div
            v-if="character.clickToExpandImage"
            style="
              display: flex;
              flex-flow: column nowrap;
              justify-content: center;
              align-items: center;
              gap: 16px;
            "
          >
            <img
              v-if="character.expandedImage"
              class="form-image-character"
              :src="resolveAssetPath(character.expandedImage, 'characters')"
              style="object-fit: contain; max-width: 200px;"
            >
            <asset-select-input
              label
              v-model="character.expandedImage"
              legacy-manifest-path="characters"
            >Expanded Image (optional, defaults to the unexpanded image)</asset-select-input>
          </div>

          <div
            style="
              display: flex;
              flex-flow: column nowrap;
              justify-content: center;
              gap: 16px;
            "
          >
            <checkbox-input
              v-if="character.clickToExpandImage && character.expandedImage"
              v-model="character.expandedView360"
            >Enable 360 Image View Support For Expanded Image</checkbox-input>

            <select-input
              v-if="character.expandedView360"
              v-model="character.expandedView360ProjectionType"
              label
              disable-sort
              default-value="Equirectangular"
              :options="['Equirectangular', 'Cylindrical']"
            >
              Projection Type
            </select-input>
          </div>
        </div>

        <div style="width: 100%; text-align: center;">
          <strong>NOTE:</strong> Default Offsets are used with the Default Image or Angled Image.
          <br>
          <strong>NOTE:</strong> For X Offsets, this is the additional distance <strong>away</strong> from the side the character appears.
        </div>
        <div style="display: flex; flex-direction: row; flex-wrap: wrap; justify-content: space-around;">
          <text-input
            label
            v-model.trim="character.xOffset"
            placeholder="X Offset (optional)"
            no-count
          >
            Default X Offset (specify CSS units, e.g. 50px)
          </text-input>
          <text-input
            label
            v-model.trim="character.yOffset"
            placeholder="Y Offset (optional)"
            no-count
          >
            Default Y Offset (specify CSS units, e.g. 50px)
          </text-input>
        </div>
      </div>
    </div>
  </template>

  <template v-if="character.avatar || character.narrator || character.scrollImage">
    <div class="form-row">
      <img v-if="character.imageAngled"
        class="form-image-character"
        :src="resolveAssetPath(character.imageAngled, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <asset-select-input label v-model="character.imageAngled"  legacy-manifest-path="characters">Angled Image (for talking to another character)</asset-select-input>
    </div>
  </template>

  <template v-if="character.avatar || character.narrator">
    <array-input :items="character.imageVariants" :disabled="validator.imageVariants.$invalid && character.imageVariants.length > 0">
      <h5 slot="title">
        Image Variants
        <br>
        <span style="font-size: 75%;">
          Selectable in Narrative inputs by the name.
          <br>
          Recommended that variant names are kept consistent across characters for maximum convenience when using player checkboxes or the narrator fallback.
        </span>
      </h5>

      <div class="form-row" slot="item" slot-scope="{ item: imageVariant, index: i }">
        <img
          v-if="imageVariant.image"
          class="form-image-background"
          :src="resolveAssetPath(imageVariant.image, 'characters')"
          style="object-fit: contain; max-width: 144px;"
        />
        <div class="col">
          <div style="display: flex; flex-direction: row; flex-wrap: wrap; justify-content: space-around;">
            <asset-select-input
              label
              v-model="imageVariant.image"
              :validator="validator.imageVariants.$each[i].image"
              legacy-manifest-path="characters"
              required
            >
              Image
            </asset-select-input>
            <text-input
              label
              v-model.trim="imageVariant.name"
              :validator="validator.imageVariants.$each[i].name"
              required
              :remove="() => { character.imageVariants.splice(i, 1); }"
            >
              Name
            </text-input>
          </div>
          <div style="width: 100%; text-align: center;">
            <strong>NOTE:</strong> For X Offsets, this is the additional distance <strong>away</strong> from the side the character appears.
          </div>
          <div style="display: flex; flex-direction: row; flex-wrap: wrap; justify-content: space-around;">
            <checkbox-input v-model="imageVariant.noAnimation">
              Never Animate
            </checkbox-input>
            <checkbox-input v-model="imageVariant.noAnimationEnter">
              Never Animate Entering
            </checkbox-input>
            <checkbox-input v-model="imageVariant.noAnimationLeave">
              Never Animate Leaving
            </checkbox-input>
            <text-input
              label
              v-model.trim="imageVariant.xOffset"
              placeholder="X Offset (optional)"
              no-count
            >
              X Offset (specify CSS units, e.g. 50px)
            </text-input>
            <text-input
              label
              v-model.trim="imageVariant.yOffset"
              placeholder="Y Offset (optional)"
              no-count
            >
              Y Offset (specify CSS units, e.g. 50px)
            </text-input>
          </div>
          <div style="display: flex; flex-direction: row; flex-wrap: wrap; justify-content: space-around;">
            <text-input
              label
              v-model.trim="imageVariant.speechXOffset"
              placeholder="Speech Bubble X Offset (optional)"
              no-count
            >
              Speech Bubble X Offset (specify CSS units, e.g. 50px)
            </text-input>
            <text-input
              label
              v-model.trim="imageVariant.speechYOffset"
              placeholder="Speech Bubble Y Offset (optional)"
              no-count
            >
              Speech Bubble Y Offset (specify CSS units, e.g. 50px)
            </text-input>
          </div>
        </div>
      </div>
      <span slot="button">Add Image Variant</span>
    </array-input>
  </template>

  <template v-if="character.avatar">
    <div class="form-row">
      <img v-if="character.imageSelected"
        class="form-image-character"
        :src="resolveAssetPath(character.imageSelected, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <asset-select-input v-model="character.imageSelected"  legacy-manifest-path="characters">Selected Image</asset-select-input>
    </div>
  </template>

  <template v-if="character.avatar">
    <div class="form-row">
      <img v-if="character.imageUnselected"
        class="form-image-character"
        :src="resolveAssetPath(character.imageUnselected, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <asset-select-input v-model="character.imageUnselected"  legacy-manifest-path="characters">Unselected Image</asset-select-input>
    </div>
  </template>

  <template v-if="character.avatar || character.narrator || character.challengeIntro">
    <div class="form-row">
      <img v-if="character.icon"
        class="form-image-character"
        :src="resolveAssetPath(character.icon, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <asset-select-input v-model="character.icon"  legacy-manifest-path="characters">Default Icon</asset-select-input>
    </div>
  </template>

  <template v-if="character.avatar">
    <div class="form-row">
      <img v-if="character.iconDecided"
        class="form-image-character"
        :src="resolveAssetPath(character.iconDecided, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <asset-select-input v-model="character.iconDecided"  legacy-manifest-path="characters">Decided Icon</asset-select-input>
    </div>
  </template>

  <template v-if="character.avatar">
    <div class="form-row">
      <img v-if="character.iconUndecided"
        class="form-image-character"
        :src="resolveAssetPath(character.iconUndecided, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <asset-select-input v-model="character.iconUndecided"  legacy-manifest-path="characters">Undecided Icon</asset-select-input>
    </div>
  </template>

  <template v-if="character.challengeIntro">
    <div class="form-row">
      <img v-if="character.imageChallengeIntro"
        class="form-image-character"
        :src="resolveAssetPath(character.imageChallengeIntro, 'characters')"
        style="object-fit: contain; max-width: 200px;"
      >
      <asset-select-input v-model="character.imageChallengeIntro"  legacy-manifest-path="characters">Section Intro Image</asset-select-input>
    </div>
  </template>

  <voice-config-editor v-if="character !== characterStub" :config="character"></voice-config-editor>
</template>
</edit-page>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';


import EditPage from '../../ui/pages/EditPage';

import ArrayInput from '../../ui/inputs/ArrayInput';
import CheckboxInput from '../../ui/inputs/CheckboxInput';
import AssetSelectInput from '../../ui/inputs/AssetSelectInput';
import SelectInput from '../../ui/inputs/SelectInput';
import TextInput from '../../ui/inputs/TextInput';
import VoiceConfigEditor from './components/VoiceConfigEditor';

export default {
  components: {
    EditPage,
    CheckboxInput,
    AssetSelectInput,
    SelectInput,
    TextInput,
    VoiceConfigEditor,
    ArrayInput
  },
  computed: {
    aliasList() {
      const aliases = this.aliases.map(a => a.path);
      if (aliases.length > 0) {
        return aliases.join('\n');
      }
      return 'No aliases found.  Create aliases in the Character Explorer.';
    }
  },
  data() {
    return {
      utils: {
      },
      characterStub: {
        _id: '',
        name: '',
        title: '',
        avatar: false,
        narrator: false,
        challengeIntro: false,
        scrollImage: false,
        twoDimensional: false,
        theme: 'any',
        bio: '',
        image: '',
        xOffset: '',
        yOffset: '',
        imageAngled: '',
        imageVariants: [],
        imageSelected: '',
        imageUnselected: '',
        voice: null,
        googleTtsAudioConfig: {
          audioEncoding: {
            speakingRate: 'MP3',
            pitch: 0,
            volumeGainDb: 0,
            effectsProfileId: [],
          }
        },
        icon: '',
        iconDecided: '',
        iconUndecided: '',
        imageChallengeIntro: ''
      },
      validator: {
        name: { required },
        title: { required },
        imageVariants: {
          $each: {
            image: { required },
            name: { required }
          }
        }
      },
      themeOptions: [],
      aliases: []
    };
  },
  methods: {
    ...mapActions([
      'getCharacter',
      'getCharacterAliases',
      'getCharacterEnums',
      'createCharacter',
      'updateCharacter',
      'copyCharacterToCentralDb',
      'deleteCharacter'
    ]),
    async centralizeChar(char) {
      await this.copyCharacterToCentralDb(char);
      window.location.reload();
    },
    async updateCharacterWrapper(char) {
      if (char && char.isOldCharacter) {
        // eslint-disable-next-line no-alert
        alert('This character has yet not been moved to the centralized character repository and is therefore read-only.  Please either move this character to the centralized repository or replace it with a character from the centralized repository.');
        throw new Error();
      }
      return this.updateCharacter(char);
    }
  },
  async mounted() {
    const { themes } = await this.getCharacterEnums();
    const aliases = await this.getCharacterAliases({ _id: this.$route.params.characterId });

    this.themeOptions = themes;
    this.aliases = aliases;
  },
};
</script>

<style lang="scss" scoped>
.form-image-character + span {
  display: block;
  margin: -10px 0 10px 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/blade/pages/CharacterEdit.vue